import { Link } from 'react-router-dom'
import { Helmet } from 'react-helmet-async'
import { ArrowRight, Check, MapPin, ShieldCheck, Building2, Calendar } from 'lucide-react'
import { DARK, TEAL, BG, MarketingNavBar, MarketingFooter, FaqAccordion, faqJsonLd, KeywordCrossLinks } from '../../components/MarketingLayout'

const FAQS = [
  {
    q: 'Ez ugyanaz, mint a HACCP igazolás?',
    a: 'A gyakorlatban sokan HACCP igazolásnak hívják ezt a papírt, amikor egy konkrét szállítmányhoz kéri az óvoda, iskola vagy megrendelő. Hivatalosan ez inkább eredetigazolás vagy származási igazolás — a HACCP-rendszered nyomonkövethetőségi kötelezettségéből (852/2004/EK) következik. A teljes HACCP dokumentáció (veszélyelemzés, CCP-k, napi napló) ennél nagyobb dokumentumcsomag — ha arra van szükséged, nézd meg a HACCP igazolás és napló oldalt. Ha már vezeted a HACCP dokumentációt a CukrászDoc-ban, ez a szállításonkénti igazolás ugyanabból az adatból egy kattintással elkészül.',
  },
  {
    q: 'Mikor kell eredetigazolás / származási igazolás?',
    a: 'Ha közétkeztetésbe (óvoda, iskola, bölcsőde), rendezvényre vagy más vállalkozásnak szállítasz saját készítésű terméket, a megrendelő gyakran kéri ezt a papírt annak bizonyítására, hogy hol és milyen körülmények között készült a termék — ez a nyomonkövethetőségi kötelezettséged igazolása.',
  },
  {
    q: 'Mi a különbség a gyártmánylap és az eredetigazolás között?',
    a: 'A gyártmánylap a termék belső, technológiai dokumentuma (receptúra, tápérték, allergének). Az eredetigazolás a megrendelő felé igazolja, hogy ki, hol és mikor állította elő a terméket — céges adatokkal, kelt dátummal, aláírással.',
  },
  {
    q: 'Milyen adatok szerepelnek rajta?',
    a: 'Termék neve, összetétele, allergénjelölés, tárolási előírás, a cég neve, székhelye és telephelye, kapcsolattartó, a kiállítás kelte és helye, valamint aláírás/pecsét helye.',
  },
  {
    q: 'Automatikusan kitölti a dátumot a rendszer?',
    a: 'Igen, a kiállítás dátuma és a cégadatok automatikusan bekerülnek a PDF-be a beállított céginformációk alapján — nincs kézi gépelés.',
  },
]

export default function SzarmazasiIgazolasPage() {
  return (
    <div style={{ fontFamily: 'Inter, system-ui, sans-serif' }}>
      <Helmet>
        <title>HACCP igazolás szállításhoz — eredetigazolás óvodának, iskolának — CukrászDoc</title>
        <meta name="description" content="Amit sokan HACCP igazolásnak hívnak: eredetigazolás (származási igazolás) sütemény, torta szállításához óvodának, iskolának vagy rendezvényre — automatikusan a receptből, egy kattintással." />
        <link rel="canonical" href="https://cukraszdoc.hu/szarmazasi-igazolas" />
        <script type="application/ld+json">{JSON.stringify(faqJsonLd(FAQS))}</script>
      </Helmet>
      <MarketingNavBar />

      <section style={{ background: `linear-gradient(160deg, ${DARK} 0%, #162119 100%)`, paddingTop: '5rem' }} className="relative overflow-hidden">
        <div className="relative max-w-4xl mx-auto px-6 py-16 lg:py-24">
          <div className="inline-flex items-center gap-2 mb-6 px-3 py-1.5 rounded-full text-xs font-semibold"
            style={{ background: 'rgba(91,184,160,0.15)', color: TEAL, border: '1px solid rgba(91,184,160,0.3)' }}>
            <MapPin size={12} />
            Amit sokan HACCP igazolásnak hívnak
          </div>
          <h1 className="text-4xl sm:text-5xl font-bold leading-tight text-white mb-5">
            HACCP igazolás <span style={{ color: TEAL }}>szállításhoz</span>
          </h1>
          <p className="text-lg sm:text-xl max-w-2xl leading-relaxed mb-8" style={{ color: 'rgba(255,255,255,0.6)' }}>
            Óvodába, iskolába vagy rendezvényre szállítasz saját készítésű süteményt, tortát, és a megrendelő "HACCP igazolást" kér hozzá? Hivatalosan ez eredetigazolás (más néven származási igazolás) — a CukrászDoc a recept adataiból automatikusan elkészíti, céges adatokkal és dátummal kitöltve.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/register" className="inline-flex items-center gap-2 px-6 py-3.5 rounded-xl font-semibold text-base transition-all" style={{ background: TEAL, color: '#fff' }}>
              Próbáld ki ingyen <ArrowRight size={16} />
            </Link>
          </div>
        </div>
        <div className="relative" style={{ height: 60, marginBottom: -2 }}>
          <svg viewBox="0 0 1440 60" preserveAspectRatio="none" className="absolute bottom-0 w-full h-full">
            <path d="M0,30 C360,60 1080,0 1440,30 L1440,60 L0,60 Z" fill="#ffffff" />
          </svg>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-4xl mx-auto px-6">
          <h2 className="text-3xl font-bold mb-6" style={{ color: DARK }}>Mi az a HACCP igazolás / eredetigazolás, és mi köze a HACCP-hez?</h2>
          <p className="text-base leading-relaxed text-gray-600 mb-4">
            A nyomonkövethetőség — vagyis hogy bármikor visszakereshető legyen, honnan jött és ki állította elő az élelmiszer — a HACCP-alapú élelmiszerbiztonsági rendszer egyik alappillére (852/2004/EK). A megrendelők (óvoda, iskola, rendezvényszervező) ezt a papírt a hétköznapokban gyakran egyszerűen "HACCP igazolásnak" hívják, hivatalosan azonban eredetigazolás vagy származási igazolás a neve: azt tanúsítja, hogy egy adott termék hol, melyik vállalkozásnál és milyen körülmények között készült.
          </p>
          <p className="text-base leading-relaxed text-gray-600">
            Közétkeztetési intézmények (óvoda, iskola, bölcsőde), rendezvényszervezők és viszonteladók gyakran kérik ezt a dokumentumot minden szállított tétel mellé. Kézzel elkészíteni ezt minden egyes szállításhoz időigényes — a CukrászDoc a már felvitt recept és céges adatok alapján, ugyanabból az adatból, amiből a <Link to="/haccp-igazolas" style={{ color: TEAL, fontWeight: 600 }}>HACCP dokumentációdat</Link> is vezeted, egy kattintással legenerálja a nyomtatható PDF-et. (Ha kíváncsi vagy, miért nevezik ezt sokan HACCP igazolásnak, itt <Link to="/blog/mit-jelent-a-haccp-igazolas" style={{ color: TEAL, fontWeight: 600 }}>elmagyarázzuk</Link>.)
          </p>
        </div>
      </section>

      <section style={{ background: BG }} className="py-20">
        <div className="max-w-6xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-12" style={{ color: DARK }}>Mit tartalmaz a HACCP igazolás / eredetigazolás?</h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              { icon: Building2, title: 'Céges adatok', desc: 'Cégnév, székhely, telephely, kapcsolattartó.' },
              { icon: ShieldCheck, title: 'Termékadatok', desc: 'Összetétel, allergének, tárolási előírás.' },
              { icon: Calendar, title: 'Kiállítás kelte', desc: 'Automatikusan kitöltve a gyártás dátumával.' },
              { icon: Check, title: 'Aláírás / pecsét helye', desc: 'Nyomtatás után helyben aláírható, lepecsételhető.' },
            ].map(({ icon: Icon, title, desc }) => (
              <div key={title} className="bg-white rounded-2xl p-6" style={{ border: '1px solid rgba(91,184,160,0.15)' }}>
                <Icon size={22} style={{ color: TEAL }} className="mb-3" />
                <h3 className="font-semibold mb-2 text-base" style={{ color: DARK }}>{title}</h3>
                <p className="text-sm text-gray-500 leading-relaxed">{desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      <section className="py-20 bg-white">
        <div className="max-w-2xl mx-auto px-6">
          <h2 className="text-3xl font-bold text-center mb-10" style={{ color: DARK }}>Gyakori kérdések a HACCP igazolásról / eredetigazolásról</h2>
          <FaqAccordion items={FAQS} />
        </div>
      </section>

      <KeywordCrossLinks current="/szarmazasi-igazolas" />

      <section style={{ background: DARK }} className="py-20">
        <div className="max-w-2xl mx-auto px-6 text-center">
          <h2 className="text-3xl font-bold text-white mb-4">Kezdd el ma — ingyen</h2>
          <p className="mb-8 text-base" style={{ color: 'rgba(255,255,255,0.55)' }}>Regisztrálj, add meg a céges adataidat, és percek alatt kész a származási igazolásod.</p>
          <Link to="/register" className="inline-flex items-center gap-2 px-8 py-4 rounded-xl font-semibold text-base" style={{ background: TEAL, color: '#fff' }}>
            <Check size={17} /> Ingyenes regisztráció
          </Link>
        </div>
      </section>

      <MarketingFooter />
    </div>
  )
}
